/**
 * Pure helpers for the survey builder.
 *
 * Everything here is framework free so the builder rules (normalization,
 * validation and reordering) can be unit tested without mounting a component.
 * The normalization mirrors `SurveyForm#normalize_questions` so what the
 * operator previews in the dashboard is exactly what the API persists.
 */
import {
  CHOICE_QUESTION_TYPES,
  DEFAULT_TEXT_MAX_LENGTH,
  LEASE_BOUND_OPERATIONAL_FLOWS,
  NUMBER_BOUND_QUESTION_TYPES,
  operationalFlowForKind,
  PLACEHOLDER_QUESTION_TYPES,
  SCORED_QUESTION_TYPES,
  SURVEY_KINDS,
  SURVEY_OPERATIONAL_FLOWS,
  SURVEY_QUESTION_TYPES,
  TEXT_QUESTION_TYPES,
} from '../constants';

export const MIN_CHOICE_OPTIONS = 2;

export const isResubmitLockedFlow = flow =>
  LEASE_BOUND_OPERATIONAL_FLOWS.includes(flow);
export const DEFAULT_RATING_MAX = 5;
export const MIN_RATING_MAX = 2;
export const MAX_RATING_MAX = 10;
export const MAX_EXPIRES_IN_DAYS = 365;
export const MAX_TEXT_MAX_LENGTH = 5000;
export const MAX_PLACEHOLDER_LENGTH = 255;
export const VISIBILITY_SOURCE_TYPES = Object.freeze([
  'single_choice',
  'multi_choice',
  'boolean',
]);
const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;

export const isChoiceType = type => CHOICE_QUESTION_TYPES.includes(type);
export const isScoredType = type => SCORED_QUESTION_TYPES.includes(type);
export const isTextType = type => TEXT_QUESTION_TYPES.includes(type);
export const supportsMaxLength = type => isTextType(type);
export const supportsPlaceholder = type =>
  PLACEHOLDER_QUESTION_TYPES.includes(type);
export const supportsNumberBounds = type =>
  NUMBER_BOUND_QUESTION_TYPES.includes(type);
export const supportsScale = type => type === 'rating';
export const supportsVisibilitySource = type =>
  VISIBILITY_SOURCE_TYPES.includes(type);
export const isValidQuestionType = type => SURVEY_QUESTION_TYPES.includes(type);
export const isValidKind = kind => SURVEY_KINDS.includes(kind);
export const isValidOperationalFlow = flow =>
  SURVEY_OPERATIONAL_FLOWS.includes(flow);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toPositiveInteger = value => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeNumberBound = value => {
  if (value === null || value === undefined || value === '') return null;
  const candidate = String(value).trim();
  if (!DECIMAL_PATTERN.test(candidate)) return String(value);
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : String(value);
};

const parseNumberBound = value => {
  const candidate = String(value).trim();
  if (!DECIMAL_PATTERN.test(candidate)) return null;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Accent-free, url-safe token used as the stable value of a choice option. */
export const slugify = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Scale bounds per question type. NPS and Likert are fixed by the model, only
 * `rating` lets the operator pick how many stars the customer sees.
 */
export const scaleForType = (type, scale = {}) => {
  switch (type) {
    case 'nps':
      return { min: 0, max: 10 };
    case 'likert':
      return { min: 1, max: 5 };
    case 'rating': {
      const max = Number.parseInt(scale?.max, 10);
      return {
        min: 1,
        max: Number.isFinite(max)
          ? clamp(max, MIN_RATING_MAX, MAX_RATING_MAX)
          : DEFAULT_RATING_MAX,
      };
    }
    default:
      return {};
  }
};

/** Next free `qN` key, so duplicated/reordered questions never collide. */
export const buildQuestionKey = (existingKeys = []) => {
  const taken = new Set(existingKeys.filter(Boolean).map(String));
  let index = taken.size + 1;
  while (taken.has(`q${index}`)) index += 1;
  return `q${index}`;
};

export const createOption = (label = '', value = '') => ({
  value: value || slugify(label),
  label,
});

export const normalizeOption = (option, index = 0) => {
  const raw =
    option && typeof option === 'object'
      ? option
      : { value: option, label: option };
  const label =
    raw.label === null || raw.label === undefined ? '' : String(raw.label);
  const value = raw.value ? String(raw.value) : slugify(label);
  return { value: value || `opcao-${index + 1}`, label };
};

export const normalizeVisibility = visibility => {
  const raw = visibility && typeof visibility === 'object' ? visibility : null;
  if (!raw) return null;

  const questionKey = String(raw.question_key ?? '');
  const value = String(raw.value ?? '');
  if (!questionKey && !value) return null;

  return { question_key: questionKey, value };
};

export const visibilityValuesFor = question => {
  if (question?.type === 'boolean') {
    return [
      { value: 'true', label: 'Sim' },
      { value: 'false', label: 'Não' },
    ];
  }
  if (!isChoiceType(question?.type)) return [];

  return (question.options || []).map(normalizeOption);
};

export const isQuestionVisible = (question, answers = {}) => {
  const visibility = normalizeVisibility(question?.visible_when);
  if (!visibility) return true;
  if (!visibility.question_key || !visibility.value) return false;

  const raw = answers?.[visibility.question_key];
  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(String)
    .includes(visibility.value);
};

export const createQuestion = ({ type = 'rating', existingKeys = [] } = {}) => {
  const questionType = isValidQuestionType(type) ? type : 'rating';
  return {
    key: buildQuestionKey(existingKeys),
    type: questionType,
    label: '',
    help: '',
    required: true,
    options: isChoiceType(questionType) ? [createOption(), createOption()] : [],
    scale: scaleForType(questionType),
    max_length: supportsMaxLength(questionType)
      ? DEFAULT_TEXT_MAX_LENGTH
      : null,
    placeholder: '',
    min: null,
    max: null,
    visible_when: null,
    position: 0,
  };
};

export const normalizeQuestion = (question, index = 0) => {
  const raw = question && typeof question === 'object' ? question : {};
  const type = isValidQuestionType(raw.type) ? raw.type : 'text';
  const options = isChoiceType(type)
    ? (Array.isArray(raw.options) ? raw.options : []).map(normalizeOption)
    : [];
  const maxLength = supportsMaxLength(type)
    ? toPositiveInteger(raw.max_length) || DEFAULT_TEXT_MAX_LENGTH
    : null;
  const placeholder = supportsPlaceholder(type)
    ? String(raw.placeholder ?? '')
    : '';

  return {
    key: raw.key ? String(raw.key) : `q${index + 1}`,
    type,
    label:
      raw.label === null || raw.label === undefined ? '' : String(raw.label),
    help: raw.help === null || raw.help === undefined ? '' : String(raw.help),
    required: raw.required === true || raw.required === 'true',
    options,
    scale: scaleForType(type, raw.scale),
    max_length: maxLength,
    placeholder,
    min: supportsNumberBounds(type) ? normalizeNumberBound(raw.min) : null,
    max: supportsNumberBounds(type) ? normalizeNumberBound(raw.max) : null,
    visible_when: normalizeVisibility(raw.visible_when),
    position: index,
  };
};

export const normalizeQuestions = questions =>
  (Array.isArray(questions) ? questions : []).map(normalizeQuestion);

export const normalizeSuccessScreen = (screen = {}) => {
  const raw = screen && typeof screen === 'object' ? screen : {};
  return {
    title: raw.title ? String(raw.title) : '',
    message: raw.message ? String(raw.message) : '',
    cta_label: raw.cta_label ? String(raw.cta_label) : '',
    cta_url: raw.cta_url ? String(raw.cta_url) : '',
    show_score: raw.show_score === true || raw.show_score === 'true',
  };
};

export const normalizeSettings = (settings = {}, kind = 'custom') => {
  const raw = settings && typeof settings === 'object' ? settings : {};
  const requestedFlow = raw.operational_flow
    ? String(raw.operational_flow)
    : '';
  const operationalFlow = isValidOperationalFlow(requestedFlow)
    ? requestedFlow
    : operationalFlowForKind(kind);
  return {
    expires_in_days: toPositiveInteger(raw.expires_in_days),
    allow_resubmit: isResubmitLockedFlow(operationalFlow)
      ? false
      : raw.allow_resubmit === true || raw.allow_resubmit === 'true',
    locale: raw.locale ? String(raw.locale) : '',
    theme: raw.theme ? String(raw.theme) : '',
    operational_flow: operationalFlow,
  };
};

export const normalizeForm = (form = {}) => {
  const raw = form && typeof form === 'object' ? form : {};
  const kind = isValidKind(raw.kind) ? raw.kind : 'custom';
  return {
    id: raw.id ?? null,
    name: raw.name ? String(raw.name) : '',
    slug: raw.slug ? String(raw.slug) : '',
    kind,
    description: raw.description ? String(raw.description) : '',
    questions: normalizeQuestions(raw.questions),
    success_screen: normalizeSuccessScreen(raw.success_screen),
    settings: normalizeSettings(raw.settings, kind),
    active: raw.active !== false,
    default_for_kind: raw.default_for_kind === true,
    responses_count: Number(raw.responses_count) || 0,
    submitted_count: Number(raw.submitted_count) || 0,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
};

export const createEmptyForm = ({ kind = 'visit' } = {}) => ({
  ...normalizeForm({ kind, active: true }),
  questions: [{ ...createQuestion({ type: 'rating' }), required: true }],
  success_screen: normalizeSuccessScreen({ show_score: true }),
  settings: normalizeSettings({ allow_resubmit: false }, kind),
});

const withPosition = (question, index) => ({ ...question, position: index });

/** Renumber `position` after any structural change to the list. */
export const reindexQuestions = questions =>
  (Array.isArray(questions) ? questions : []).map(withPosition);

export const moveQuestion = (questions, fromIndex, toIndex) => {
  const list = Array.isArray(questions) ? [...questions] : [];
  if (fromIndex < 0 || fromIndex >= list.length) return reindexQuestions(list);

  const target = clamp(toIndex, 0, list.length - 1);
  if (target === fromIndex) return reindexQuestions(list);

  const [moved] = list.splice(fromIndex, 1);
  list.splice(target, 0, moved);
  return reindexQuestions(list);
};

export const moveQuestionBy = (questions, index, delta) =>
  moveQuestion(questions, index, index + delta);

export const removeQuestion = (questions, index) => {
  const list = Array.isArray(questions) ? [...questions] : [];
  if (index < 0 || index >= list.length) return reindexQuestions(list);
  list.splice(index, 1);
  return reindexQuestions(list);
};

export const addQuestion = (questions, type = 'rating') => {
  const list = Array.isArray(questions) ? [...questions] : [];
  const question = createQuestion({
    type,
    existingKeys: list.map(item => item?.key),
  });
  return reindexQuestions([...list, question]);
};

export const duplicateQuestion = (questions, index) => {
  const list = Array.isArray(questions) ? [...questions] : [];
  const source = list[index];
  if (!source) return reindexQuestions(list);

  const copy = {
    ...normalizeQuestion(source, index),
    key: buildQuestionKey(list.map(item => item?.key)),
    options: (source.options || []).map(option => ({ ...option })),
  };
  list.splice(index + 1, 0, copy);
  return reindexQuestions(list);
};

/**
 * Rewrites a question when the operator switches its type: type-specific
 * fields are rebuilt so a rating never keeps orphan options and a choice never
 * keeps an orphan scale.
 */
export const changeQuestionType = (question, type) => {
  const nextType = isValidQuestionType(type) ? type : 'text';
  const base = question && typeof question === 'object' ? question : {};
  const options = isChoiceType(nextType)
    ? (base.options || []).map(normalizeOption)
    : [];

  while (isChoiceType(nextType) && options.length < MIN_CHOICE_OPTIONS) {
    options.push(createOption());
  }

  return {
    ...base,
    type: nextType,
    options,
    scale: scaleForType(nextType, base.scale),
    max_length: supportsMaxLength(nextType)
      ? toPositiveInteger(base.max_length) || DEFAULT_TEXT_MAX_LENGTH
      : null,
    placeholder: supportsPlaceholder(nextType)
      ? String(base.placeholder ?? '')
      : '',
    min: supportsNumberBounds(nextType) ? normalizeNumberBound(base.min) : null,
    max: supportsNumberBounds(nextType) ? normalizeNumberBound(base.max) : null,
  };
};

export const isValidHttpUrl = value => {
  const candidate = String(value ?? '').trim();
  if (!candidate) return false;
  return /^https?:\/\/[^\s]+\.[^\s]+/i.test(candidate);
};

/**
 * Per-question validation. Returns an object of error CODES (never copy) so
 * the Vue layer keeps the i18n ownership.
 */
export const validateQuestion = (
  question,
  { questions = [], index = 0 } = {}
) => {
  const errors = {};
  const raw = question && typeof question === 'object' ? question : {};

  if (!String(raw.label ?? '').trim()) errors.label = 'LABEL_REQUIRED';
  if (!isValidQuestionType(raw.type)) errors.type = 'TYPE_INVALID';

  if (isChoiceType(raw.type)) {
    const options = Array.isArray(raw.options) ? raw.options : [];
    const filled = options.filter(option => String(option?.label ?? '').trim());
    if (filled.length < MIN_CHOICE_OPTIONS) errors.options = 'OPTIONS_MIN';
    else if (filled.length !== options.length)
      errors.options = 'OPTION_LABEL_REQUIRED';
  }

  if (supportsScale(raw.type)) {
    const max = Number.parseInt(raw.scale?.max, 10);
    if (!Number.isFinite(max) || max < MIN_RATING_MAX || max > MAX_RATING_MAX) {
      errors.scale = 'SCALE_INVALID';
    }
  }

  // The character limit is optional: only a filled-in value has to be sane.
  const hasMaxLength =
    raw.max_length !== null &&
    raw.max_length !== undefined &&
    raw.max_length !== '';
  if (supportsMaxLength(raw.type) && hasMaxLength) {
    const maxLength = Number.parseInt(raw.max_length, 10);
    if (
      !Number.isFinite(maxLength) ||
      maxLength < 1 ||
      maxLength > MAX_TEXT_MAX_LENGTH
    ) {
      errors.max_length = 'MAX_LENGTH_INVALID';
    }
  }

  if (supportsPlaceholder(raw.type)) {
    const placeholder = String(raw.placeholder ?? '').trim();
    if (placeholder.length > MAX_PLACEHOLDER_LENGTH) {
      errors.placeholder = 'PLACEHOLDER_INVALID';
    }
  }

  if (supportsNumberBounds(raw.type)) {
    const hasMin = raw.min !== null && raw.min !== undefined && raw.min !== '';
    const hasMax = raw.max !== null && raw.max !== undefined && raw.max !== '';
    const min = hasMin ? parseNumberBound(raw.min) : null;
    const max = hasMax ? parseNumberBound(raw.max) : null;
    if (
      (hasMin && !Number.isFinite(min)) ||
      (hasMax && !Number.isFinite(max))
    ) {
      errors.number = 'NUMBER_BOUNDS_INVALID';
    } else if (min !== null && max !== null && min > max) {
      errors.number = 'NUMBER_BOUNDS_INVALID';
    }
  }

  const visibility = normalizeVisibility(raw.visible_when);
  if (visibility) {
    const sourceIndex = questions.findIndex(
      candidate => candidate?.key === visibility.question_key
    );
    const source = sourceIndex >= 0 ? questions[sourceIndex] : null;
    if (!source || sourceIndex >= index) {
      errors.visible_when = 'VISIBILITY_SOURCE_INVALID';
    } else if (!supportsVisibilitySource(source.type)) {
      errors.visible_when = 'VISIBILITY_TYPE_INVALID';
    } else if (
      !visibilityValuesFor(source).some(
        option => option.value === visibility.value
      )
    ) {
      errors.visible_when = 'VISIBILITY_VALUE_INVALID';
    }
  }

  return errors;
};

/**
 * Whole-form validation. `question_errors` is keyed by question key so the
 * editor can highlight a specific card even after a reorder.
 */
export const validateForm = form => {
  const raw = form && typeof form === 'object' ? form : {};
  const questions = Array.isArray(raw.questions) ? raw.questions : [];
  const errors = {
    name: '',
    kind: '',
    questions: '',
    success_screen: {},
    settings: {},
    question_errors: {},
  };

  if (!String(raw.name ?? '').trim()) errors.name = 'NAME_REQUIRED';
  if (!isValidKind(raw.kind)) errors.kind = 'KIND_INVALID';

  if (!questions.length) {
    errors.questions = 'QUESTIONS_MIN';
  } else {
    const keys = questions.map(question => question?.key);
    if (new Set(keys).size !== keys.length)
      errors.questions = 'QUESTIONS_DUPLICATE_KEY';
  }

  questions.forEach((question, index) => {
    const questionErrors = validateQuestion(question, { questions, index });
    if (Object.keys(questionErrors).length) {
      errors.question_errors[question?.key || `q${index + 1}`] = questionErrors;
    }
  });

  const successScreen = raw.success_screen || {};
  if (!String(successScreen.title ?? '').trim()) {
    errors.success_screen.title = 'SUCCESS_TITLE_REQUIRED';
  }
  const ctaLabel = String(successScreen.cta_label ?? '').trim();
  const ctaUrl = String(successScreen.cta_url ?? '').trim();
  if (ctaLabel && !ctaUrl) errors.success_screen.cta_url = 'CTA_URL_REQUIRED';
  else if (ctaUrl && !isValidHttpUrl(ctaUrl))
    errors.success_screen.cta_url = 'CTA_URL_INVALID';
  if (ctaUrl && !ctaLabel)
    errors.success_screen.cta_label = 'CTA_LABEL_REQUIRED';

  const expiresInDays = raw.settings?.expires_in_days;
  if (
    expiresInDays !== null &&
    expiresInDays !== undefined &&
    expiresInDays !== ''
  ) {
    const parsed = Number.parseInt(expiresInDays, 10);
    if (
      !Number.isFinite(parsed) ||
      parsed < 1 ||
      parsed > MAX_EXPIRES_IN_DAYS
    ) {
      errors.settings.expires_in_days = 'EXPIRES_INVALID';
    }
  }

  return errors;
};

export const hasValidationErrors = errors => {
  if (!errors) return false;
  if (errors.name || errors.kind || errors.questions) return true;
  if (Object.keys(errors.success_screen || {}).length) return true;
  if (Object.keys(errors.settings || {}).length) return true;
  return Object.keys(errors.question_errors || {}).length > 0;
};

export const countValidationErrors = errors => {
  if (!errors) return 0;
  const scalars = [errors.name, errors.kind, errors.questions].filter(Boolean);
  const nested = [
    ...Object.values(errors.success_screen || {}),
    ...Object.values(errors.settings || {}),
  ].filter(Boolean);
  const perQuestion = Object.values(errors.question_errors || {}).reduce(
    (total, questionErrors) => total + Object.keys(questionErrors).length,
    0
  );
  return scalars.length + nested.length + perQuestion;
};

/** API payload — drops UI-only fields and read-only counters. */
export const buildFormPayload = form => {
  const normalized = normalizeForm(form);
  return {
    name: normalized.name.trim(),
    kind: normalized.kind,
    description: normalized.description.trim(),
    active: normalized.active,
    default_for_kind: normalized.default_for_kind,
    questions: normalized.questions.map((question, index) => ({
      key: question.key,
      type: question.type,
      label: question.label.trim(),
      help: question.help.trim(),
      required: question.required,
      options: question.options
        .filter(option => String(option.label ?? '').trim())
        .map(option => ({
          value: option.value || slugify(option.label),
          label: option.label.trim(),
        })),
      scale: question.scale,
      max_length: question.max_length,
      placeholder: supportsPlaceholder(question.type)
        ? question.placeholder.trim()
        : '',
      min: supportsNumberBounds(question.type) ? question.min : null,
      max: supportsNumberBounds(question.type) ? question.max : null,
      position: index,
      ...(question.visible_when ? { visible_when: question.visible_when } : {}),
    })),
    success_screen: normalized.success_screen,
    settings: normalized.settings,
  };
};

/** Percentage of delivered surveys that were answered (0-100, integer). */
export const responseRate = form => {
  const sent = Number(form?.responses_count) || 0;
  const submitted = Number(form?.submitted_count) || 0;
  if (!sent) return 0;
  return Math.round((submitted / sent) * 100);
};

/**
 * Client-side list filtering. The dashboard fetches every form of the account
 * (a settings-sized collection) and filters locally so the operator gets
 * instant feedback while typing.
 */
export const filterForms = (forms, { kind = '', active = '', q = '' } = {}) => {
  const term = String(q ?? '')
    .trim()
    .toLowerCase();

  return (Array.isArray(forms) ? forms : []).filter(form => {
    if (kind && form?.kind !== kind) return false;
    if (active === 'active' && form?.active !== true) return false;
    if (active === 'inactive' && form?.active !== false) return false;
    if (!term) return true;

    return [form?.name, form?.description, form?.slug]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(term));
  });
};
